import { useLoadScript } from '@react-google-maps/api';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import crossWhite from '../../assets/img/icons/remove_assignee.svg';
import BackButtonComponent from '../../components/BackButtonComponent/BackButtonComponent';
import AddSingleMemberDropdown from '../../components/CustomDropdown/AddSingleMemberDropdown';
import { CardText } from '../../components/DashboardCards/cardStyles';
import CreateProjectDropdown from '../../components/DropdownStyle/CreateProjectDropdown';
import ComponentCircleLoader from '../../components/Loaders/ComponentCircleLoader';
import Select from '../../components/ReactSelectDropdown';
import { ClearSearchButton } from '../../components/Search/searchStyles';
import DatePicker from '../../components/SingleDatePicker/SingleDatePicker';
import useAutocompleteService from '../../customHooks/usePlacesAutocompleteService';
import { ListUpperSection } from '../../styledComponents/ProjectsNew';
import {
  ButtonIcon,
  ColoredButton,
  ColoredButtonWithIcon,
  PrimaryButton,
  RadiusButton,
  WhiteButton,
} from '../../styledComponents/buttons';
import {
  AssignListText,
  ColoredText,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  Container,
  ContentHeader,
  FormAlert,
  HeaderContainer,
  InputField,
  InputLabel,
  InputWithLabelSection,
  PageTitle,
  RedStar,
} from '../../styledComponents/common';
import { checkHtml, checkUrl } from '../../utils/helper';
import { DataAndMapContainer } from '../FieldServices/JobSites/jobSitesStyles';
import { MapAndAlertContainer } from '../FieldServices/RouteMap/routeMapStyles';
import { CommImageNameComp } from '../Members/CommonComponents';
import TimeRangeComponent from '../Timesheet/Daily/TimeRangeComponent';
import AddOffice from './AddOffice';
import DeleteOfficeModal from './DeleteOfficeModal';
import OfficeDetails from './OfficeDetails';
import OfficeList from './OfficesList';
import OfficesMap from './OfficesMap';
import {
  InfoMapSection,
  InfoSection,
  Tabsection,
  TitleTag,
} from './officesStyles';

const mapLibraries = ['places'];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const Offices = ({
  getMonazimList,
  monazimList,
  isLoading,
  getOfficesList,
  officesList,
  officeDetails,
  setOfficeDetails,
  history,
  clearOfficeCreatedUpdated,
  createdOffice,
  addOfficeAssignee,
  removeOfficeAssignee,
  isAssigneesLoading,
  createOffice,
  isCreateOfficeLoading,
}) => {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState('list');
  //search
  const [searchTerm, setSearchTerm] = useState('');
  const [currentSearchedTerm, setCurrentSearchedTerm] = useState('');
  // delete
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteOfficeId, setDeleteOfficeId] = useState(null);

  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // details states
  const [singleAddressMarker, setSingleAddressMarker] = useState(null);
  const [addressInput, setAddressInput] = useState('');
  const [radiusInput, setRadiusInput] = useState(100);

  // map info
  const [isMapInfoVisible, setIsMapInfoVisible] = useState(true);

  // map states
  const [map, setMap] = useState(null);

  const [userCurrentLocation, setUserCurrentLocation] = useState(null);
  // pagination
  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  const { isLoaded, loadError } = useLoadScript({
    id: 'offices-map-script-loader',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });

  const {
    geocoderService,
    getPlacePredictions,
    placePredictions,
    setPlacePredictions,
    isPredictionsLoading,
    refreshSessionToken,
  } = useAutocompleteService({ map, debounceTimeout: 500 });

  useEffect(() => {
    getOfficesList({
      page: null,
      office_name: null,
    });
  }, []);

  useEffect(() => {
    if (currentPage && currentPage === 'list') {
      clearOfficeCreatedUpdated();
    }
  }, [currentPage]);

  useEffect(() => {
    if (officesList && officesList.length > 0 && currentPage === 'list') {
      // console.log(officesList);
      // setSelectedOffice(officesList[0]);
      if (map) {
        handleFitBounds(officesList);
      }
    }
  }, [officesList, map]);

  const handleFitBounds = list => {
    if (map) {
      if (list.length === 1) {
        // If there's only one office, check if it has locations
        const office = list[0];
        if (office.locations && office.locations.length > 0) {
          // Use the first location or selected location for this office
          const locationToUse = office.locations[0];
          const location = {
            lat: parseFloat(locationToUse.latitude),
            lng: parseFloat(locationToUse.longitude),
          };
          handleMapPanAndZoomToSingleLocation(location);
        } else if (office.latitude && office.longitude) {
          // Fallback to the office location if no locations array
          const location = {
            lat: parseFloat(office.latitude),
            lng: parseFloat(office.longitude),
          };
          handleMapPanAndZoomToSingleLocation(location);
        }
      } else {
        const bounds = new window.google.maps.LatLngBounds();

        // Loop through all offices and their locations
        list.forEach(office => {
          if (office.locations && office.locations.length > 0) {
            // Add each location to the bounds
            office.locations.forEach(location => {
              const position = new window.google.maps.LatLng(
                parseFloat(location.latitude),
                parseFloat(location.longitude),
              );
              bounds.extend(position);
            });
          } else if (office.latitude && office.longitude) {
            // Fallback to office coordinates if no locations
            const position = new window.google.maps.LatLng(
              parseFloat(office.latitude),
              parseFloat(office.longitude),
            );
            bounds.extend(position);
          }
        });

        map.fitBounds(bounds, 20);
      }
    }
  };

  const handleGetGeocode = place => {
    if (geocoderService && place && place.place_id && map) {
      setAddressInput(place.description);
      geocoderService
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          handleSetAddressMarker(results[0].geometry.location);
        })
        .catch(e => {
          console.log(e);
        });
      setPlacePredictions([]);
    }
    refreshSessionToken();
  };
  const handleReverseGeocode = coord => {
    if (geocoderService && coord) {
      setPlacePredictions([]);
      geocoderService
        .geocode({ location: coord })
        .then(results => {
          if (results.results && results.results.length) {
            setAddressInput(results.results[0].formatted_address);
          } else {
            setAddressInput(`${coord.lat()}, ${coord.lng()}`);
          }
        })
        .catch(e => {
          console.log(e);
        });
    }
  };

  // const handleSearchByName = () => {
  //   const payload = {
  //     page: null,
  //   };
  //   if (searchTerm && searchTerm.trim()) {
  //     payload.office_name = searchTerm.toLowerCase().trim();
  //     setCurrentSearchedTerm(searchTerm);
  //   } else {
  //     setCurrentSearchedTerm('');
  //   }
  //   getOfficesList(payload);
  //   setCurrentPageNumber(1);
  // };
  // const handleClearSearch = () => {
  //   if (currentSearchedTerm) {
  //     const payload = {
  //       page: null,
  //     };
  //     getOfficesList(payload);
  //     setCurrentPageNumber(1);
  //   }
  //   setTimeout(() => {
  //     setSearchTerm('');
  //     setCurrentSearchedTerm('');
  //   }, 100);
  // };

  const onSearchTermChange = e => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  const handleChangePage = (page, officeData) => {
    setCurrentPage(page);
    if (page === 'details') {
      if (officeData && officeData.id) {
        setOfficeDetails(officeData);
      } else {
        setSingleAddressMarker(null);
      }
    } else {
      if (officesList && officesList.length > 0) {
        setSelectedOffice(null);
        setSelectedLocation(null);
        if (map) {
          handleFitBounds(officesList);
        }
      }
    }
  };

  const handleSelectOffice = (office, location = null) => {
    if (office && office.id) {
      setSelectedOffice(office);

      if (location) {
        // If a specific location is selected
        setSelectedLocation(location);

        if (map) {
          map.panTo({
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
          });
          map.setZoom(17);
        }
      } else if (office.locations && office.locations.length > 0) {
        // Default to first location if none specified
        setSelectedLocation(office.locations[0]);

        if (map) {
          map.panTo({
            lat: parseFloat(office.locations[0].latitude),
            lng: parseFloat(office.locations[0].longitude),
          });
          map.setZoom(17);
        }
      } else if (office.latitude && office.longitude) {
        // Fallback to office coordinates if no locations
        setSelectedLocation(null);

        if (map) {
          map.panTo({
            lat: parseFloat(office.latitude),
            lng: parseFloat(office.longitude),
          });
          map.setZoom(17);
        }
      }
    }
  };

  const handleSetAddressFromMap = location => {
    if (location && location.latLng && currentPage === 'details') {
      handleSetAddressMarker(location.latLng);
      handleReverseGeocode(location.latLng);
    }
  };
  const handleSetAddressMarker = location => {
    if (location) {
      setSingleAddressMarker(location);

      handleMapPanAndZoomToSingleLocation(location);
    }
  };
  const handleMapPanAndZoomToSingleLocation = location => {
    if (map) {
      map.panTo(location);
      map.setZoom(17);
    }
  };

  const toggleDeleteModal = officeId => {
    // if (!isDeleteModalVisible) {
    //   setDeleteOfficeId(officeId);
    // }
    setIsDeleteModalVisible(!isDeleteModalVisible);
  };

  return (
    <div className="content">
      {/* <DeleteOfficeModal
        isOpen={isDeleteModalVisible}
        toggle={() => setIsDeleteModalVisible(!isDeleteModalVisible)}
        // handleDelete={handleDeleteOffice}
        isLoading={false}
      /> */}

      <PageTitle>{t('offices')}</PageTitle>
      {currentPage === 'details' && (
        <ContentHeader>
          <BackButtonComponent
            onClick={() => handleChangePage('list')}
            subTitle={t('back_to_all_offices')}
          />
        </ContentHeader>
      )}
      <Container padding="0">
        <DataAndMapContainer
          columns={currentPage === 'details' ? '100%' : '35% 65%'}
        >
          {currentPage === 'list' ? (
            <OfficeList
              handleChangePage={handleChangePage}
              isLoading={isLoading}
              officesList={officesList}
              selectedOffice={selectedOffice}
              selectedLocation={selectedLocation}
              selectOffice={handleSelectOffice}
              toggleDeleteModal={toggleDeleteModal}
              searchTerm={searchTerm}
              onSearchTermChange={onSearchTermChange}
            />
          ) : currentPage === 'details' ? (
            currentPage === 'add' ? (
              <AddOffice
                createOffice={createOffice}
                isLoading={isCreateOfficeLoading}
              />
            ) : (
              <OfficeDetails
                handleChangePage={handleChangePage}
                officeDetails={officeDetails}
                addressMarker={singleAddressMarker}
                setAddressMarker={handleSetAddressMarker}
                addressInput={addressInput}
                setAddressInput={setAddressInput}
                radiusInput={radiusInput}
                setRadiusInput={setRadiusInput}
                // autocomplete service
                getGeocode={handleGetGeocode}
                getPlacePredictions={getPlacePredictions}
                placePredictions={placePredictions}
                isPredictionsLoading={isPredictionsLoading}
                toggleDeleteModal={toggleDeleteModal}
                orgMembersList={monazimList}
                getMonazimList={getMonazimList}
                createdOffice={createdOffice}
                addOfficeAssignee={addOfficeAssignee}
                removeOfficeAssignee={removeOfficeAssignee}
                isAssigneesLoading={isAssigneesLoading}
              />
            )
          ) : null}
          {currentPage !== 'details' && (
            <>
              {loadError ? (
                <div>
                  Map cannot be loaded right now! Please try again later
                </div>
              ) : isLoaded ? (
                <MapAndAlertContainer>
                  <OfficesMap
                    currentPage={currentPage}
                    sitesList={officesList}
                    activeSite={selectedOffice}
                    activeLocation={selectedLocation}
                    selectSite={handleSelectOffice}
                    singleAddressMarker={singleAddressMarker}
                    setAddressFromMap={handleSetAddressFromMap}
                    setMap={setMap}
                    radius={radiusInput}
                  />
                </MapAndAlertContainer>
              ) : (
                <ComponentCircleLoader />
              )}
            </>
          )}
        </DataAndMapContainer>
      </Container>
    </div>
  );
};

export default Offices;
